import { useTranslate } from 'react-admin'
import { CertificateOut, CertificateStatus, CertificateType, TargetSystem } from '../../types/apiRequests'
import { getCommonName } from '../../utils/getCommonName'

export interface TargetSystemCapabilities {
  // 'manual': user enters metaData.commonName; 'derived': the system's own form sets it.
  commonName: 'manual' | 'derived'
  allowedTypes: CertificateType[]
  // Certificate can be installed (scheduled/now) into the target system.
  installable: boolean
}

// Mirrors the API's target system registry (GET /target-systems).
// Record over the enum: a new TargetSystem without an entry fails tsc.
export const TARGET_SYSTEMS: Record<TargetSystem, TargetSystemCapabilities> = {
  [TargetSystem.ZAKEN]: {
    commonName: 'derived',
    allowedTypes: [CertificateType.Pki, CertificateType.ZaakClient],
    installable: true,
  },
  [TargetSystem.WAARDEREN]: {
    commonName: 'manual',
    allowedTypes: [CertificateType.Pki],
    installable: false,
  },
  [TargetSystem.HEFFEN_EN_INNEN]: {
    commonName: 'manual',
    allowedTypes: [CertificateType.Pki],
    installable: false,
  },
}

export const getCapabilities = (targetSystem?: TargetSystem | null): TargetSystemCapabilities | undefined =>
  // eslint-disable-next-line security/detect-object-injection
  targetSystem ? TARGET_SYSTEMS[targetSystem] : undefined

// Statuses before the CSR left the system: a downloaded CSR keeps the old hostname.
export const COMMON_NAME_EDITABLE_STATUSES: CertificateStatus[] = [
  CertificateStatus.PENDING,
  CertificateStatus.CSR_READY,
]

// Select/filter choices: id plus the i18n key of its label.
export const targetSystemChoices = Object.values(TargetSystem).map(id => ({
  id,
  name: `resources.certificates.targetSystems.${id.toLowerCase()}`,
}))

export const typeChoices = Object.values(CertificateType).map(id => ({
  id,
  name: `resources.certificates.types.${id}`,
}))

/** Translated target system and type of a certificate, and its title (hostname, else "Zaken PKI"). */
export const useCertificateLabels = (record?: CertificateOut): { system: string; type: string; title: string } => {
  const translate = useTranslate()
  const systemKey = targetSystemChoices.find(choice => choice.id === record?.targetSystem)?.name
  const typeKey = typeChoices.find(choice => choice.id === record?.type)?.name
  const system = systemKey ? translate(systemKey) : ''
  const type = typeKey ? translate(typeKey) : ''
  return { system, type, title: getCommonName(record) || [system, type].filter(Boolean).join(' ') }
}
